/**
 * useDRBFMSessions - Session List Management
 *
 * Hook for fetching, listing, and deleting DRBFM sessions.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from './redux';
import {
  selectSessions,
  selectSessionsLoading,
  selectSessionsError,
  clearSessionsError,
} from '../features/drbfm/drbfmSlice';
import { drbfmService } from '../services/drbfm.service';
import { DRBFMSession } from '../types';
import { resolveApiErrorMessage } from '../utils/apiErrorMessages';

export const useDRBFMSessions = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const sessions = useAppSelector(selectSessions);
  const isLoading = useAppSelector(selectSessionsLoading);
  const error = useAppSelector(selectSessionsError);

  const [localSessions, setLocalSessions] = useState<DRBFMSession[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  // Store the wire error, translate at render. Putting `t` in these callbacks'
  // deps recreates them on every language change, and DRBFMHistoryPage keys an
  // effect on fetchSessions — so a toggle refetched the whole list.
  const [localFailure, setLocalFailure] = useState<{ err: unknown; fallbackKey: string } | null>(null);

  // Fetch sessions from API
  const fetchSessions = useCallback(async (limit = 20, offset = 0) => {
    setLocalLoading(true);
    setLocalFailure(null);

    try {
      const response = await drbfmService.listSessions({ limit, offset });
      setLocalSessions(response.sessions);
      return response;
    } catch (err) {
      setLocalFailure({ err, fallbackKey: 'drbfm.errors.sessionsFetchFailed' });
      throw err;
    } finally {
      setLocalLoading(false);
    }
  }, []);

  // Delete a session
  const deleteSession = useCallback(async (sessionId: string) => {
    try {
      await drbfmService.deleteSession(sessionId);
      setLocalSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch (err) {
      setLocalFailure({ err, fallbackKey: 'drbfm.errors.sessionDeleteFailed' });
      throw err;
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setLocalFailure(null);
    dispatch(clearSessionsError());
  }, [dispatch]);

  return {
    sessions: localSessions.length > 0 ? localSessions : sessions,
    isLoading: localLoading || isLoading,
    error: localFailure
      ? resolveApiErrorMessage(t, localFailure.err, { fallbackKey: localFailure.fallbackKey })
      : error,
    fetchSessions,
    deleteSession,
    clearError,
  };
};
